import React from 'react';

interface AboutViewProps {
  onClose: () => void;
}

export const AboutView: React.FC<AboutViewProps> = ({ onClose }) => {
  return (
    <div className="about-view">
      <div className="about-header">
        <button onClick={onClose} className="back-button">
          ← Back
        </button>
        <h2>About Šāʾilu</h2>
      </div>

      <div className="about-content">
        <section className="about-section">
          <h3>What is Šāʾilu?</h3>
          <p>
            The <em>šāʾilu</em> (𒊭𒄿𒇽, Akkadian: &ldquo;questioner&rdquo;) was a dream diviner in
            ancient Mesopotamia who interpreted dreams as omens. This system brings that practice into
            the modern era by matching your dreams with authentic historical interpretations spanning
            over 3,000 years using natural language processing and semantic search.
          </p>
        </section>

        <section className="about-section">
          <h3>How It Works</h3>
          
          <h4 style={{ fontSize: 'var(--text-base)', fontWeight: '600', marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>General Overview</h4>
          <p>
            When you enter a dream, the system breaks it into meaningful segments and searches through
            a corpus of historical texts to find the most relevant ancient interpretations. Each match
            shows you which parts of your dream connected to which ancient omens, along with their
            historical significance and recommended protective rituals.
          </p>
          
          <h4 style={{ fontSize: 'var(--text-base)', fontWeight: '600', marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>Technical Overview</h4>
          <p>The analysis pipeline combines several NLP and machine learning techniques:</p>
          <ul>
            <li>
              <strong>Dream Segmentation:</strong> Your dream is parsed into clauses using
              conjunctive detection, enabling granular symbol-level matching
            </li>
            <li>
              <strong>Semantic Search:</strong> Each segment is embedded using all-MiniLM-L6-v2
              (384-dimensional vectors) and matched against 1,068 historical records via cosine
              similarity
            </li>
            <li>
              <strong>Quality-Gated RRF:</strong> Results are fused using Reciprocal Rank Fusion with
              adaptive thresholding to balance multi-segment coverage with match quality
            </li>
            <li>
              <strong>Historical Sentiment Analysis:</strong> Outcomes are evaluated using a
              Gemma 3 model fine-tuned on Egyptian dream book sentiments and generalized to ancient
              Near Eastern cultural values (not modern psychological frameworks)
            </li>
            <li>
              <strong>Token Attribution:</strong> SHAP-style ablation analysis highlights which
              words in each interpretation drove the semantic match
            </li>
            <li>
              <strong>2D Ritual Matching:</strong> Protective rituals are recommended based on both
              semantic relevance and sentiment compatibility
            </li>
          </ul>
          <p>
            All processing happens in your browser using ONNX models and precomputed embeddings—no
            server calls, no data transmission.
          </p>

          <p style={{ marginTop: 'var(--space-4)' }}>
            <strong>Corpus Size:</strong> 549 dream omens, 497 encyclopedia entries, 18 rituals
          </p>
        </section>

        <section className="about-section">
          <h3>Historical Sources</h3>
          <p>All interpretations come from authentic ancient texts:</p>
          
          <h4 style={{ fontSize: 'var(--text-base)', fontWeight: '600', marginTop: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>Dream Interpretation Collections</h4>
          <ul>
            <li>
              <strong>Assyrian Dream Book</strong> (350 omens) - Neo-Assyrian dream interpretation
              manual, 7th century BCE
            </li>
            <li>
              <strong>Egyptian Dream Book</strong> (163 omens) - Papyrus Chester Beatty III and
              related texts, 12th century BCE
            </li>
            <li>
              <strong>Susa Dream Collection</strong> (36 omens) - Persian period dream omens from Susa
            </li>
          </ul>

          <h4 style={{ fontSize: 'var(--text-base)', fontWeight: '600', marginTop: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>Contextual Encyclopedia (497 entries)</h4>
          <ul>
            <li>
              <strong>Symbols, Materials & Deities</strong> - Dream symbols, objects, and divine
              figures with interpretation-focused descriptions
            </li>
            <li>
              <strong>Mesopotamian Ritual Texts</strong> - 18 protective procedures from KAR and STT
              cuneiform collections
            </li>
            <li>
              <strong>Historical Dream Accounts</strong> - 38 recorded dreams from royal
              inscriptions, temple records, and epic literature (including Gilgamesh)
            </li>
          </ul>

          <h4 style={{ fontSize: 'var(--text-base)', fontWeight: '600', marginTop: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>Further Reading</h4>
          <ul>
            <li>
              <strong>Electronic Text Corpus of Sumerian Literature</strong> (ETCSL) - 162 Sumerian
              and Akkadian literary texts from Oxford, providing cultural context for the ancient worldview
            </li>
          </ul>
        </section>

        <section className="about-section">
          <h3>Privacy</h3>
          <p>
            Your dreams are stored locally in your browser using IndexedDB. No data is sent to any
            server. You can delete your dream history at any time. All AI processing happens
            client-side using ONNX models loaded in your browser.
          </p>
        </section>

        <section className="about-section">
          <h3>Purpose & Disclaimer</h3>
          <p>
            Šāʾilu is a showcase demonstrating how historical data can be combined with modern NLP
            and semantic technologies to recreate an authentic ancient practice. It is not a research
            tool, psychological counseling service, or predictive divination system—it is an
            exploration of how machine learning can make ancient interpretive traditions accessible
            and engaging.
          </p>
          <p>
            <strong>Important Note:</strong> The interpretations provided may sometimes not make
            complete sense or seem disconnected from your dream. This is an inherent limitation of
            working with fragmentary ancient texts that were written thousands of years ago in very
            different cultural contexts. The ancient dream omens that survived are limited in number,
            often preserved incompletely, and reflect worldviews quite different from modern ones.
            The system does its best to find meaningful matches, but the nature of the historical
            source material means some results will be more relevant than others.
          </p>
        </section>

        <section className="about-section">
          <h3>Contact</h3>
          <p>
            For questions, feedback, or inquiries, reach out to:{' '}
            <a href="mailto:contact@gbe.games" className="contact-link">
              contact@gbe.games
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};
